///
/// EarthBrowser - GeoPackage File Handler
///
/// Handles GeoPackage (.gpkg) drag & drop
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { GeoPackageDataProvider } from '@/data-providers/GeoPackageDataProvider'
import { GeoPackageCatalogTable } from '@/earthbrowser/database/tables/GeoPackageCatalogTable'
import { GeoPackageLoader } from '@/earthbrowser/geopackage/GeoPackageLoader'
import { getGeoPackageManager } from '@/earthbrowser/geopackage/GeoPackageManager'
import { logger } from '@/utils/LogDispatcher'
import { getOrCreateDatasetWindow } from '@/utils/windowUtils'
import type { GeoPackageCatalog, GeoPackageLayer } from '@shared-types/geopackage-catalog'
import { BaseFileHandler } from './BaseFileHandler'

export class GeoPackageFileHandler extends BaseFileHandler {
	canHandle(file: File): boolean {
		return file.name.toLowerCase().endsWith('.gpkg')
	}

	initialize(): void {
		// No initialization needed for GeoPackage handler
	}

	cleanup(): void {
		// No cleanup needed for GeoPackage handler
	}

	async handleFile(file: File): Promise<string | null> {
		// Get or create window for this file
		const windowId = getOrCreateDatasetWindow(
			this.context.getState,
			this.context.dispatch,
			file.name,
			'geopackage',
			{ width: 900, height: 600 }
		)

		// If window already exists, do nothing
		if (!windowId) {
			console.log(`Window already exists for ${file.name}`)
			return null
		}

		try {
			// Read file data
			const arrayBuffer = await file.arrayBuffer()
			const data = new Uint8Array(arrayBuffer)

			// Generate a unique filename
			// Sanitize the filename to avoid issues with hyphens, spaces, and special characters
			// that can confuse SQLite's WAL file naming (-wal, -shm suffixes)
			// Use a shorter unique ID (base36 random + shortened timestamp)
			const uniqueId = Math.random().toString(36).substring(2, 8) // 6 chars
			const sanitizedName = file.name
				.replace(/\.(gpkg)$/i, '') // Remove extension
				.toLowerCase()
				.replace(/[^a-z0-9_]/g, '_') // Replace non-alphanumeric (except underscore) with underscore
				.replace(/__+/g, '_') // Replace multiple underscores with single
				.replace(/^_|_$/g, '') // Remove leading/trailing underscores
				.substring(0, 20) // Limit to 20 chars (aggressive truncation)

			// Format: import_{uniqueId}_{sanitizedName}.gpkg
			// Example: import_a3x9k2_tz_world.gpkg (much shorter!)
			const filename = `import_${uniqueId}_${sanitizedName}.gpkg`

			// Save to OPFS
			await GeoPackageLoader.saveToOPFS(filename, data)

			// Verify the file exists and is accessible
			const exists = await GeoPackageLoader.exists(filename)
			if (!exists) {
				throw new Error('File was not properly saved to OPFS')
			}

			// Small delay to ensure OPFS has fully committed the file
			await new Promise(resolve => setTimeout(resolve, 100))

			// Create catalog entry (marked as temporary)
			const now = new Date().toISOString()
			const catalogId = `import_${uniqueId}` // Use the same unique ID as the filename
			const catalogEntry: GeoPackageCatalog = {
				id: catalogId,
				package_id: catalogId,
				name: file.name.replace('.gpkg', ''),
				description: `Imported from drag & drop on ${new Date().toLocaleDateString()}`,
				version: '1.0.0',
				category: 'user',
				subcategory: null,
				tags: ['imported', 'user-data', 'temporary'],
				filename: filename,
				file_size: file.size,
				file_hash: null,
				bbox_min_x: null,
				bbox_min_y: null,
				bbox_max_x: null,
				bbox_max_y: null,
				coordinate_system: 'EPSG:4326',
				min_zoom: null,
				max_zoom: null,
				feature_count: null,
				cdn_url: '',
				mirror_urls: [],
				source: 'User Import',
				attribution: null,
				attribution_url: null,
				license: null,
				license_url: null,
				visibility: 'private',
				requires_subscription: false,
				subscription_tier: null,
				created_by: null,
				organization: null,
				status: 'active',
				quality_score: null,
				temporary: true, // Mark as temporary - will be removed on browser close unless installed
				download_count: 0,
				install_count: 0,
				rating_avg: null,
				rating_count: 0,
				created_at: now,
				updated_at: now,
				last_downloaded_at: null,
				metadata: {
					imported: true,
					importDate: now,
					originalFilename: file.name
				}
			}

			// Add to catalog
			await GeoPackageCatalogTable.insert(catalogEntry)

			// Load the package to verify it's valid
			const gpkgManager = getGeoPackageManager()
			await gpkgManager.loadInstalledPackage(catalogEntry)

			// Get the database instance from the manager
			const db = gpkgManager.getPackage(catalogId)
			if (!db) {
				throw new Error(`Failed to get GeoPackage database: ${catalogId}`)
			}

			// Get available feature layers
			const layerInfo = await db.getLayerInfo()

			if (layerInfo.length === 0) {
				throw new Error('No feature layers found in GeoPackage')
			}

			// Use the first feature layer
			const firstLayer = layerInfo[0]
			const tableName = firstLayer.table_name

			// Get feature count for all layers
			// NOTE: getLayerInfo() rows don't structurally match GeoPackageLayer; the
			// cast preserves the existing (unchanged) property access behavior
			const layersWithCounts = await Promise.all(
				layerInfo.map(async (layer) => {
					const catalogLayer = layer as unknown as GeoPackageLayer
					return {
						tableName: catalogLayer.layer_name,
						featureCount: await db.getFeatureCount(catalogLayer.layer_name),
						dataType: catalogLayer.layer_type,
						identifier: catalogLayer.id,
						description: catalogLayer.layer_name,
					}
				})
			)

			// Create GeoPackageDataProvider for the first table
			const provider = new GeoPackageDataProvider({
				id: windowId,
				name: `${file.name} - ${tableName}`,
				database: db,
				tableName: tableName,
			})

			// Register the provider - this makes it available to the window
			DataProviderRegistry.register(windowId, provider)

			// Dispatch completion event with all layer information
			window.dispatchEvent(new CustomEvent(`dataset:complete:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					catalogId,
					tableName,
					featureCount: layersWithCounts[0].featureCount,
					layers: layersWithCounts,
					windowType: 'geopackage'
				}
			}))

			// Also dispatch the legacy event for backward compatibility
			window.dispatchEvent(new CustomEvent('geopackage:imported', {
				detail: {
					package: catalogEntry
				}
			}))

			console.log(`GeoPackage imported: ${file.name} (${layersWithCounts.length} layers, ${layersWithCounts[0].featureCount} features in ${tableName})`)
			return windowId
		} catch (error) {
			logger.error('Failed to import GeoPackage:', error)

			// Dispatch error event
			window.dispatchEvent(new CustomEvent(`dataset:error:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					error: error instanceof Error ? error.message : String(error)
				}
			}))

			throw error
		}
	}
}

